import { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { ImageIcon, Upload, Loader2, ArrowLeft, Expand, FilesIcon, CameraIcon, Pencil, Camera } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { EntityWidgetProps } from "@/other/constants/EntityManagerConfig";
import { DashboardCard } from "@/modern/components/dashboard-card";
import { CardTitle } from "@/modern/components/ui/card";
import { Badge } from "@/modern/components/ui/badge";
import { Button } from "@/modern/components/ui/button-shadcn";
import { ButtonGroup } from "@/modern/components/ui/button-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { MoreMenu, type MenuItem } from "@/modern/components/more-menu";
import FilePreview from "@/modern/components/file-preview";
import { useEntityFileContext, type FormattedFile } from "@/react/components/entityViews/files/context/EntityFileContext";
import { FileDetailModal } from "@/react/components/entityViews/files/components/editors/FileDetailModal/FileDetailModal";
import { CompanyCamProjectEditor } from "@/react/components/entityViews/files/components/editors/CompanyCamProjectEditor";
import { CompanyCamButton } from "@/react/components/entityViews/files/components/CompanyCamButton";
import { isImageFile, isMediaFile } from "@/other/utils/utils";
import { useEntityFileUpload } from "@/react/components/entityViews/files/hooks/useEntityFileUpload";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { BoardFile } from "@/bryntum-lib/models/BoardFile";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { cn } from "@/modern/utils";
import InfiniteScroll from "@/modern/components/infinite-scroll";
import { ImageZoom } from "@/modern/components/image-zoom";
import { FileCollectionNavButtons } from "@/react/components/entityViews/files/components/FileCollectionNavButtons";
import { useFileNavigation, useFileNavigationKeyboard, useFocusedNavigationContainer } from "@/react/components/entityViews/files/components/editors/FileDetailModal/hooks/useFileNavigation";

const transition = { duration: 0.15, ease: "easeInOut" as const };

const GRID_COLS = 3;
const GRID_GAP = 6;   // gap-1.5 = 0.375rem = 6px
const GRID_PAD = 12;  // p-3 = 0.75rem = 12px

const MediaGridCell = ({
  file,
  onClick,
}: {
  file: FormattedFile;
  onClick: (file: FormattedFile) => void;
}) => {
  const src = file.originalImageUrl || file.thumbnailUrl;

  return (
    <button
      type="button"
      onClick={() => onClick(file)}
      className={cn(
        "group relative aspect-square overflow-hidden rounded-md bg-muted cursor-pointer border transition-all duration-200",
        file.isAnnotated
          ? "border-primary/40 hover:border-primary hover:shadow-primary/30"
          : "border-transparent hover:border-primary/50"
      )}
    >
      {file.record?.isPhantom ? (
        <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
          <span className="text-[10px]">Uploading...</span>
        </div>
      ) : (
        <>
          <FilePreview
            file={file}
            src={src}
            className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
            showName={false}
            fileIconClassName="size-10"
            fileIconContainerClassName="w-full h-full"
            openFileOnClick={false}
            isThumbnail={true}
          />

          {file.isAnnotated && (
            <div className="absolute bottom-0 left-0 overflow-hidden w-10 h-10 pointer-events-none z-10">
              <div className="absolute -left-6 bottom-0 w-16 text-center transform rotate-45 bg-primary text-primary-foreground py-0.5 shadow-lg flex items-center justify-center">
                <Pencil className="w-2 h-2 -rotate-45" />
              </div>
            </div>
          )}

          {file.isCCImage && (
            <div className="absolute top-1 left-1 z-10 pointer-events-none">
              <div className="flex items-center justify-center size-5 rounded-full bg-black/50 backdrop-blur-sm">
                <Camera className="size-3 text-white" />
              </div>
            </div>
          )}

          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 pb-1.5 pt-6 opacity-0 group-hover:opacity-100 transition-opacity">
            <p className="truncate text-xs text-white">{file.name}</p>
          </div>
        </>
      )}
    </button>
  );
};

const MediaGrid = ({
  images,
  onSelect,
  onOpenEditor,
  scrollRef,
  hasMore,
  isLoading,
  onLoadMore,
}: {
  images: FormattedFile[];
  onSelect: (file: FormattedFile) => void;
  onOpenEditor: (file: FormattedFile) => void;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
}) => {
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [cellSize, setCellSize] = useState(100);


  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const measure = () => {
      const w = el.clientWidth;
      const available = w - GRID_PAD * 2 - GRID_GAP * (GRID_COLS - 1);
      setCellSize(Math.floor(available / GRID_COLS));
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const rowCount = Math.ceil(images.length / GRID_COLS);
  const rowHeight = cellSize + GRID_GAP;

  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 2,
  });

  useEffect(() => {
    rowVirtualizer.measure();
  }, [cellSize, rowVirtualizer]);

  const handleClick = (file: FormattedFile) => {
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
      onOpenEditor(file);
    } else {
      clickTimer.current = setTimeout(() => {
        clickTimer.current = null;
        onSelect(file);
      }, 250);
    }
  };

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalHeight = rowVirtualizer.getTotalSize();

  return (
    <div ref={containerRef} className="w-full" style={{ padding: GRID_PAD }}>
      <div className="relative w-full" style={{ height: totalHeight }}>
        {virtualRows.map((virtualRow) => {
          const startIdx = virtualRow.index * GRID_COLS;
          const rowFiles = images.slice(startIdx, startIdx + GRID_COLS);

          return (
            <div
              key={virtualRow.index}
              className="absolute left-0 top-0 w-full"
              style={{
                height: virtualRow.size,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <div
                className="grid grid-cols-3 h-full"
                style={{ gap: GRID_GAP }}
              >
                {rowFiles.map((file) => (
                  <MediaGridCell
                    key={file.id}
                    file={file}
                    onClick={handleClick}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {(hasMore || isLoading) && (
        <InfiniteScroll
          isLoading={isLoading}
          hasMore={hasMore}
          next={onLoadMore}
          rootMargin="50px"
          threshold={0}
        >
          <div className="flex items-center justify-center py-4">
            {isLoading && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
          </div>
        </InfiniteScroll>
      )}
    </div>
  );
};

export const EntityMediaView = ({ onClose, headerTitle }: EntityWidgetProps) => {
  const {
    boardFiles,
    companyCamPhotos,
    companyCamDocuments,
    totalFilesCount,
    entityRecord,
    handleFileOpen,
    openedFile,
    isFileEditorOpen,
    setIsFileEditorOpen,
    uploadNewFile,
    isFetchingPhotos,
    hasMorePhotos,
    fetchCompanyCamPhotos,
  } = useEntityFileContext();

  const { editorHandler } = useSwivuBryntumContext();
  const { subscribe: subscribeToFileChanges } = useSwivuStore<BoardFile>({ storeId: STORE_KEYS.boardFilesStore });
  const scrollRef = useRef<HTMLDivElement>(null);

  const [selectedFile, setSelectedFile] = useState<FormattedFile | null>(null);
  const isInlineView = !!selectedFile;

  const { uploadRef, isUploading, openUpload, handleUpload, accept } = useEntityFileUpload({
    category: "media",
    uploadNewFile,
  });

  useEffect(() => {
    return subscribeToFileChanges(({ record: changedRecord, action }) => {
      if (changedRecord[entityRecord.entityType] === entityRecord.id) {
        if (
          selectedFile &&
          !selectedFile.isCCImage &&
          action === "remove" &&
          changedRecord.id == selectedFile.id
        ) {
          setSelectedFile(null);
        }
      }
      return true;
    });
  }, [subscribeToFileChanges, entityRecord, selectedFile?.id, selectedFile?.isCCImage]);

  const nonPhantomBoardFiles = useMemo(() => boardFiles.filter((f) => !f.record?.isPhantom), [boardFiles]);
  const boardDocuments = useMemo(() => nonPhantomBoardFiles.filter((f) => !isMediaFile(f)), [nonPhantomBoardFiles]);
  const imageFiles = useMemo(
    () => [...nonPhantomBoardFiles.filter(isMediaFile), ...companyCamPhotos]
      .sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0)),
    [nonPhantomBoardFiles, companyCamPhotos]
  );
  const mediaCount = totalFilesCount - boardDocuments.length - companyCamDocuments.length;

  const { hasPrevious, hasNext, handlePrevious, handleNext } = useFileNavigation(
    selectedFile,
    imageFiles,
    setSelectedFile,
    { hasMore: hasMorePhotos, loadMore: fetchCompanyCamPhotos, isLoadingMore: isFetchingPhotos }
  );

  const { containerRef, focusContainer, focusContainerProps } = useFocusedNavigationContainer(isInlineView, selectedFile?.id);

  useFileNavigationKeyboard(isInlineView, containerRef, handlePrevious, handleNext);

  const handleExpandToEditor = useCallback(() => {
    if (selectedFile) {
      handleFileOpen(selectedFile);
    }
  }, [selectedFile, handleFileOpen]);

  const handleViewAllFiles = useCallback(() => {
    editorHandler.openEditor(EDITOR_KEYS.taskFilesEditor, entityRecord, { task: entityRecord, initialTab: "photos" });
  }, [editorHandler, entityRecord]);

  const menuItems: MenuItem[] = [
    {
      itemVariant: "button" as const,
      menuText: "View All Images",
      dropdownMenuItemProps: { onClick: handleViewAllFiles },
      Component: () => (
        <button className="flex items-center gap-2 w-full cursor-default">
          <FilesIcon className="size-4 mr-1" />
          View All Images
        </button>
      ),
    },
    {
      itemVariant: "modal" as const,
      variant: "title" as const,
      menuText: "Link Company Cam",
      Icon: CameraIcon,
      Component: CompanyCamProjectEditor,
      modalTitle: "Link company cam project",
      menuItemModalHeaderVariant: "title" as const,
    },
  ];

  return (
    <>
      <DashboardCard
        onClose={onClose}
        cardContentClassName={cn("h-full flex flex-col p-0", isInlineView && "!overflow-hidden")}
        header={
          <div className="flex items-center justify-between w-full gap-2">
            <CardTitle className={cn("text-base gap-3 flex items-center truncate min-w-0")}>
              {isInlineView ? (
                <>
                  <Button variant="ghost" size="icon" className="size-7 shrink-0 p-0 text-muted-foreground" onClick={() => setSelectedFile(null)}>
                    <ArrowLeft className="size-4" />
                  </Button>
                  <span className="truncate min-w-0 pr-2">{selectedFile.name}</span>
                </>
              ) : (
                <>
                  <span className="truncate">{headerTitle ?? "Media"}</span>
                  {mediaCount > 0 && (
                    <Badge variant="muted" size="xs" className="shrink-0">{mediaCount}</Badge>
                  )}
                  {isUploading && <span className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0"><Loader2 className="size-3 animate-spin" />Uploading...</span>}
                </>
              )}
            </CardTitle>

            <div className="flex items-center gap-2 shrink-0">
              {isInlineView ? (
                <ButtonGroup>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="entityHeader" className="w-auto px-3" size="icon" onClick={handleExpandToEditor}>
                        <Expand className="size-4" /> Manage
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Open in editor</TooltipContent>
                  </Tooltip>
                </ButtonGroup>
              ) : (
                <>
                  <CompanyCamButton />
                  <ButtonGroup>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="entityHeader" size="icon" onClick={openUpload}>
                          <Upload className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Upload file</TooltipContent>
                    </Tooltip>
                    <MoreMenu menuItems={menuItems} record={entityRecord} menuTitle="Media" triggerButtonVariant="entityHeader" />
                  </ButtonGroup>
                  <input ref={uploadRef} type="file" className="hidden" multiple accept={accept} onChange={handleUpload} />
                </>
              )}
            </div>
          </div>
        }
      >
        <AnimatePresence mode="wait">
          {imageFiles.length === 0 && !isInlineView ? (
            <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={transition} className="flex flex-1 items-center justify-center h-full p-4">
              <div className="h-full w-full">
                <div className="min-h-[12rem] h-full flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-muted-foreground/30 bg-muted/40 p-4 text-center shadow-sm">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <ImageIcon className="size-5" />
                  </span>
                  <div className="space-y-1">
                    <h3 className="text-sm font-semibold text-foreground">No media files yet</h3>
                    <p className="text-xs text-muted-foreground">Upload images to see them here.</p>
                  </div>
                  <Button size="sm" onClick={openUpload}>
                    <Upload className="size-4 mr-1" />
                    Upload
                  </Button>
                </div>
              </div>
            </motion.div>
          ) : isInlineView ? (
            <motion.div
              key="preview"
              {...focusContainerProps}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, transition: { duration: 0.05 } }}
              transition={transition}
              onAnimationComplete={() => {
                if (isInlineView) focusContainer();
              }}
              className="relative h-full flex items-center justify-center p-3 overflow-hidden outline-none"
            >
              <FileCollectionNavButtons
                variant="overlay"
                filesCount={imageFiles.length}
                hasPrevious={hasPrevious}
                hasNext={hasNext}
                onPrevious={handlePrevious}
                onNext={handleNext}
                isLoadingMore={isFetchingPhotos}
              />
              {isImageFile(selectedFile) ? (
                <ImageZoom zoomScale={2.5} zoomOnHover={false} zoomOnClick={true}>
                  <FilePreview
                    file={selectedFile}
                    src={selectedFile.originalImageUrl || selectedFile.thumbnailUrl}
                    className="max-w-full max-h-full object-contain rounded-md !cursor-[inherit]"
                    showName={false}
                    openFileOnClick={false}
                    disableLoadingState
                  />
                </ImageZoom>
              ) : (
                <FilePreview
                  file={selectedFile}
                  src={selectedFile.originalImageUrl || selectedFile.thumbnailUrl}
                  className="w-full h-full rounded-md"
                  showName={false}
                  openFileOnClick={false}
                  disableLoadingState
                  enableVideoAudioRendering
                />
              )}
            </motion.div>
          ) : (
            <motion.div
              ref={scrollRef}
              key="grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, x: -20 }}
              transition={transition}
              className="flex-1 overflow-y-auto overflow-x-hidden"
            >
              <MediaGrid
                images={imageFiles}
                onSelect={setSelectedFile}
                onOpenEditor={handleFileOpen}
                scrollRef={scrollRef}
                hasMore={hasMorePhotos}
                isLoading={isFetchingPhotos}
                onLoadMore={fetchCompanyCamPhotos}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </DashboardCard>

      {openedFile && isFileEditorOpen && (
        <FileDetailModal
          entityRecord={entityRecord}
          file={openedFile}
          navigationFiles={imageFiles}
          open={isFileEditorOpen}
          setOpen={setIsFileEditorOpen}
        />
      )}
    </>
  );
};
